import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Filter,
  Eye,
  Ban,
  CheckCircle2,
  RefreshCw,
  X,
  Calendar,
  Mail,
  Phone,
  ShieldCheck,
  ShieldAlert,
  Clock,
  LogOut,
  CreditCard,
  FileText
} from 'lucide-react';
import { AdminClientItem } from '../../../types/admin.types';
import {
  fetchAdminClients,
  suspendClientApi,
  reactivateClientApi,
} from '../../../lib/adminService';
import { AdminUserKycDetailsSection } from '../AdminUserKycDetailsSection';

interface ClientsTabProps {
  onShowNotice: (msg: string, type: 'success' | 'error' | 'info') => void;
  onNavigateToBookings?: (clientId: string) => void;
}

export const ClientsTab: React.FC<ClientsTabProps> = ({ onShowNotice, onNavigateToBookings }) => {
  const [clients, setClients] = useState<AdminClientItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<AdminClientItem | null>(null);

  // Suspend action modal state
  const [suspendModalClient, setSuspendModalClient] = useState<AdminClientItem | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const data = await fetchAdminClients(searchQuery, statusFilter);
      setClients(data);
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to load client accounts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, [statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadClients();
  };

  const handleConfirmSuspend = async () => {
    if (!suspendModalClient) return;
    if (!suspendReason.trim()) {
      onShowNotice('Reason for account suspension is required', 'error');
      return;
    }

    setIsSubmittingAction(true);
    try {
      await suspendClientApi(suspendModalClient.id, suspendReason);
      onShowNotice(`Client ${suspendModalClient.name} has been suspended.`, 'success');
      setSuspendModalClient(null);
      setSuspendReason('');
      loadClients();
    } catch (err: any) {
      onShowNotice(err.message || 'Error suspending account', 'error');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const handleReactivate = async (client: AdminClientItem) => {
    setIsSubmittingAction(true);
    try {
      await reactivateClientApi(client.id);
      onShowNotice(`Client ${client.name} reactivated successfully.`, 'success');
      loadClients();
    } catch (err: any) {
      onShowNotice(err.message || 'Error reactivating account', 'error');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-white tracking-tight">Client Accounts</h2>
            <span className="px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono text-xs font-bold">
              {clients.length} Total
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Section 6 & 7: Customer registry, KYC statuses, suspension controls, and booking history.
          </p>
        </div>

        {/* Search & Filter */}
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search ID, name, email..."
              className="pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-purple-500 w-48 sm:w-64"
            />
          </form>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs font-bold text-slate-300 focus:outline-hidden cursor-pointer"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
          </select>

          <button
            type="button"
            onClick={loadClients}
            className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-purple-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Client List Table (Section 6) */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-950/60 text-slate-400 font-mono uppercase text-[10px] border-b border-slate-800">
              <tr>
                <th className="py-3 px-4">Client ID</th>
                <th className="py-3 px-4">Name</th>
                <th className="py-3 px-4">Contact (Email / Phone)</th>
                <th className="py-3 px-4">Account Status</th>
                <th className="py-3 px-4">KYC Status</th>
                <th className="py-3 px-4 text-center">Bookings</th>
                <th className="py-3 px-4">Joined Date</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {clients.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-500">
                    No client records match the criteria.
                  </td>
                </tr>
              ) : (
                clients.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-3.5 px-4 font-mono font-bold text-purple-400">{c.id}</td>
                    <td className="py-3.5 px-4 font-bold text-white">{c.name}</td>
                    <td className="py-3.5 px-4">
                      <div className="text-slate-200">{c.email}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{c.phone}</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono border ${
                          c.status === 'ACTIVE'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : 'bg-rose-500/15 text-rose-400 border-rose-500/30'
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono border ${
                          c.kycStatus === 'VERIFIED'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : c.kycStatus === 'PENDING'
                            ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                            : 'bg-slate-800 text-slate-400 border-slate-700'
                        }`}
                      >
                        {c.kycStatus}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-center font-bold text-white font-mono">
                      {c.totalBookings}
                    </td>
                    <td className="py-3.5 px-4 text-slate-400 font-mono text-[11px]">
                      {new Date(c.registrationDate).toLocaleDateString()}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setSelectedClient(c)}
                          className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition cursor-pointer flex items-center gap-1 text-xs font-bold"
                          title="View Details"
                        >
                          <Eye className="w-3.5 h-3.5 text-purple-400" />
                          <span>Details</span>
                        </button>

                        {c.status === 'ACTIVE' ? (
                          <button
                            type="button"
                            onClick={() => {
                              setSuspendModalClient(c);
                              setSuspendReason('');
                            }}
                            className="p-1.5 bg-rose-950/80 hover:bg-rose-900 border border-rose-800 text-rose-300 rounded-lg transition cursor-pointer"
                            title="Suspend Client"
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleReactivate(c)}
                            className="p-1.5 bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-800 text-emerald-300 rounded-lg transition cursor-pointer"
                            title="Reactivate Client"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Client Details Modal (Section 7) */}
      {selectedClient && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto space-y-5">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                <h3 className="text-base font-black text-white">Client Profile: {selectedClient.name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedClient(null)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Client ID</span>
                <span className="font-mono font-bold text-purple-300">{selectedClient.id}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Status</span>
                <span className="font-bold text-white">{selectedClient.status}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Email</span>
                <span className="text-slate-200">{selectedClient.email}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Phone Number</span>
                <span className="text-slate-200 font-mono">{selectedClient.phone}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">KYC Status</span>
                <span className="font-bold text-white">{selectedClient.kycStatus}</span>
              </div>
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
                <span className="text-slate-500 block mb-1">Total Bookings Completed</span>
                <span className="font-bold text-emerald-400">{selectedClient.totalBookings}</span>
              </div>
            </div>

            <AdminUserKycDetailsSection
              userId={selectedClient.id}
              userName={selectedClient.name}
              userEmail={selectedClient.email}
              userRole="client"
              onReviewComplete={loadClients}
            />

            <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl text-[11px] text-slate-400">
              Last Login Recorded: {new Date(selectedClient.lastLogin).toLocaleString()} • IP & Session Audited
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setSelectedClient(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Suspend Action (Section 41) */}
      {suspendModalClient && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-rose-900/60 rounded-3xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-2 text-rose-400 font-bold text-sm">
              <ShieldAlert className="w-5 h-5" />
              <span>Confirm Client Suspension</span>
            </div>
            <p className="text-xs text-slate-300">
              Are you sure you want to suspend account <strong>{suspendModalClient.name}</strong> ({suspendModalClient.id})? They will immediately lose access to booking services.
            </p>
            <div>
              <label className="block text-[11px] font-bold text-slate-400 mb-1">
                Mandatory Reason for Suspension: *
              </label>
              <textarea
                rows={3}
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="e.g. Terms of service violation, payment chargeback dispute..."
                className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-rose-500"
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                disabled={isSubmittingAction}
                onClick={() => setSuspendModalClient(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmittingAction || !suspendReason.trim()}
                onClick={handleConfirmSuspend}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold cursor-pointer disabled:opacity-50"
              >
                {isSubmittingAction ? 'Processing...' : 'Confirm Suspension'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
